import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, Radar, RefreshCw } from "lucide-react";

const RISK_CLS = {
  high: "bg-rose-50 text-rose-800 border-rose-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function ChurnRadar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/churn-radar");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="churn-radar-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Scanning orgs for churn signals…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const allRows = data.organisations || [];
  // Prioritise high+medium risk
  const ranked = allRows.filter(r => r.risk === "high" || r.risk === "medium");
  const rows = showAll ? allRows : (ranked.length ? ranked.slice(0, 8) : allRows.slice(0, 5));
  const highCount = allRows.filter(r => r.risk === "high").length;
  const atRisk = allRows.reduce((s, r) => s + (r.monthlyValueAtRiskGBP || 0), 0);
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="churn-radar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-rose-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Churn Radar</h3>
          <span className="text-xs text-slate-500">{highCount} high-risk · {fmtGBP(atRisk)}/mo at risk</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setShowAll(s => !s)} className="h-7 px-2 text-[11px]" data-testid="churn-radar-toggle">
            {showAll ? "Show at-risk" : "Show all"}
          </Button>
          <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="churn-radar-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="churn-radar-empty">No churn signals. Fleet is healthy.</div>
      ) : (
        <div className="space-y-1.5" data-testid="churn-radar-list">
          {rows.map((o) => (
            <div key={o.organisationId} className="p-2.5 rounded-md bg-slate-50 border border-slate-100" data-testid={`churn-row-${o.organisationId}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`border text-[10px] px-1.5 py-0 ${RISK_CLS[o.risk] || RISK_CLS.medium}`}>{o.risk}</Badge>
                <span className="text-sm font-medium text-slate-900 truncate flex-1">{o.organisation}</span>
                <span className="text-[11px] font-mono text-slate-700">health {o.healthScore}</span>
                {o.monthlyValueAtRiskGBP > 0 && <span className="text-[11px] font-mono text-rose-700">{fmtGBP(o.monthlyValueAtRiskGBP)}/mo</span>}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">{o.likelyReason}</div>
              <div className="text-[11px] text-indigo-700 mt-0.5 italic">{o.suggestedIntervention}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
